import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useTranslation } from '@/config/i18n';
import { ToastContext } from '@/components/common/CommonToast';
import RestService from '@/services/RestService';
import { ObjectLiteral } from '@/types/object';
import { EXPORT_CSV_STATUS } from '@/shared/constants/csv';
import useExportCsvStore from './useExportCsvStore';
import { toastErrorMessage } from '@/lib/utils';

// --- Helper Function ---

/**
 * Downloads a file from a given URL, with an optional custom filename.
 * It attempts to fetch the file as a blob to respect the custom filename,
 * which is crucial for cross-origin URLs. Falls back to a direct link
 * if the blob method fails.
 * @param url The URL of the file to download.
 * @param filename The desired filename for the downloaded file.
 */
const downloadFile = async (url: string, filename?: string) => {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok.');

    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = objectUrl;
    if (filename) {
      link.setAttribute('download', filename);
    }

    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.error('Failed to download file via blob, falling back to direct link:', error);
    const link = document.createElement('a');
    link.href = url;
    if (filename) {
      link.setAttribute('download', filename);
    }
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

// --- Hook Props ---

type UseCsvExportProps = {
  service: RestService<any>;
  path: string;
  key: string;
  params: ObjectLiteral;
  name?: string;
  delay?: number;
  failedMessageKey?: string;
  successMessageKey?: string;
};

// --- Hook ---

const useExportCSV = ({
  service,
  path,
  params,
  key,
  name,
  delay = 3000,
  failedMessageKey = 'user_list.toast.export_csv_fail',
  successMessageKey = 'user_list.toast.export_csv_success',
}: UseCsvExportProps) => {
  const [t] = useTranslation('');
  const { toast } = useContext(ToastContext);
  const { getStatus, setStatus, removeStatus } = useExportCsvStore();

  const [isExporting, setIsExporting] = useState(false);
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController>();

  // --- State Management & Notifications ---

  const cleanup = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
    abortControllerRef.current?.abort();
    removeStatus(key);
  }, [key, removeStatus]);

  const showToast = useCallback(
    (severity: 'success' | 'error', messageKey: string) => {
      const message =
        severity === 'error'
          ? toastErrorMessage(t(messageKey))
          : { severity: 'success', summary: t(messageKey) };
      toast?.current?.show(message);
    },
    [t, toast],
  );

  // --- API Interaction ---

  const handleSuccess = useCallback(
    async (url: string) => {
      await downloadFile(url, name);
      cleanup();
      setIsExporting(false);
      showToast('success', successMessageKey);
    },
    [cleanup, name, showToast, successMessageKey],
  );

  const handleError = useCallback(() => {
    cleanup();
    setIsExporting(false);
    showToast('error', failedMessageKey);
  }, [cleanup, showToast, failedMessageKey]);

  const fetchExportStatus = useCallback(
    async (jobId: string, signal: AbortSignal) => {
      try {
        const { data } = await service.collection(`${path}/${jobId}`, {
          method: 'GET',
          signal,
        });
        return data;
      } catch (error: any) {
        const isAbortError =
          error?.name === 'AbortError' || error?.message === 'The user aborted a request.';
        if (!isAbortError) {
          handleError();
        }
        return null;
      }
    },
    [service, path, handleError],
  );

  const pollExportStatus = useCallback(
    async (jobId: string) => {
      abortControllerRef.current = new AbortController();
      // Guard Clause: Stop polling if the job has been cleared
      if (!getStatus(key)) {
        cleanup();
        setIsExporting(false);
        return;
      }

      const response = await fetchExportStatus(jobId, abortControllerRef.current.signal);
      if (!response) return;

      const { status, url } = response.data;

      switch (status) {
      case EXPORT_CSV_STATUS.COMPLETED:
        if (url) {
          await handleSuccess(url);
        } else {
          handleError(); // URL is missing
        }
        break;
      case EXPORT_CSV_STATUS.PROCESSING:
        timeoutIdRef.current = setTimeout(() => pollExportStatus(jobId), delay);
        break;
      case EXPORT_CSV_STATUS.FAILED:
        handleError();
        break;
      default:
        cleanup(); // Or handle unknown status
        setIsExporting(false);
        break;
      }
    },
    [delay, fetchExportStatus, handleSuccess, handleError, cleanup, getStatus, key],
  );

  const onExport = async (): Promise<void> => {
    if (isExporting) return;
    setIsExporting(true);

    try {
      const { data } = await service.export(params);
      const jobId = data.data?.job_id;

      if (jobId) {
        setStatus(key, jobId);
        pollExportStatus(jobId);
      } else {
        handleError();
      }
    } catch {
      handleError();
    }
  };

  // --- Effects ---

  useEffect(() => {
    const existingJobId = getStatus(key);
    if (existingJobId) {
      setIsExporting(true);
      pollExportStatus(existingJobId);
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      abortControllerRef.current?.abort();
    };
  }, [key, getStatus, pollExportStatus]);

  return {
    isExporting,
    onExport,
  };
};

export default useExportCSV;
